import Swal from 'sweetalert2';
import i18n from '../i18n';
import AuthService from '../services/auth/Auth.services';
import { GeneralQuery } from '../services/query';
import configureStore from '../stores/redux/configureStore';
import { StoreActionType } from '../stores/redux/types';
import Helper from './helper';
import { toEnglishLowerCase } from './stringHelpers';

const USER_CONTEXT_MISSING_NORMALIZED = 'usercontextmissing';

export type EnsureOperatorUsernameResult =
    | { ok: true; username: string }
    | { ok: false };

const isMissingUsername = (username?: string | null): boolean => {
    const value = (username ?? '').trim();
    return !value || value.toLowerCase() === 'unknown';
};

const extractUsernameFromOperatorData = (operatorData: Record<string, unknown> | null | undefined): string => {
    if (!operatorData || typeof operatorData !== 'object') {
        return '';
    }

    const raw =
        operatorData.username ??
        operatorData.userName ??
        operatorData.UserName ??
        operatorData.Username;

    return typeof raw === 'string' ? raw.trim() : '';
};

export const isUserContextMissingError = (errorCode?: string | number | null): boolean => {
    if (errorCode === undefined || errorCode === null || errorCode === '') {
        return false;
    }

    return String(errorCode).replace(/[_-\s]/g, '').toLowerCase() === USER_CONTEXT_MISSING_NORMALIZED;
};

let reloginInProgress = false;

/**
 * Kullanıcıya oturum eksik uyarısı gösterir ve Keycloak logout ile yeniden girişe yönlendirir.
 */
export async function forceReloginForMissingUserContext(): Promise<void> {
    if (reloginInProgress) {
        return;
    }

    reloginInProgress = true;

    try {
        await Swal.fire({
            icon: 'warning',
            title: i18n.t('mannequin.aiGeneration.errors.userContextMissingTitle'),
            text: i18n.t('mannequin.aiGeneration.errors.userContextMissing'),
            confirmButtonText: i18n.t('mannequin.aiGeneration.errors.userContextMissingConfirm'),
        });
    } finally {
        new AuthService().logout();
    }
}

/**
 * AI üretim öncesi operator username doğrular.
 * Eksik/unknown ise soft recovery (GetCheckOperatorList) dener; olmazsa hard re-login.
 * Asla "Unknown" / "unknown" dönmez.
 */
export async function ensureOperatorUsername(): Promise<EnsureOperatorUsernameResult> {
    const current = Helper.getUserName();
    if (!isMissingUsername(current)) {
        return { ok: true, username: toEnglishLowerCase(current) };
    }

    try {
        const checkOperator: any = await GeneralQuery.GetCheckOperatorList();
        if (checkOperator?.isSuccess === false || checkOperator?.IsSuccess === false) {
            await forceReloginForMissingUserContext();
            return { ok: false };
        }

        const operatorData = (checkOperator?.data || checkOperator?.Data || {}) as Record<string, unknown>;

        localStorage.setItem('checkOperator', JSON.stringify(operatorData));
        localStorage.setItem('isStoreEmployee', JSON.stringify(operatorData?.isStoreEmployee));
        localStorage.setItem('storeCode', String(operatorData?.storeCode ?? ''));

        configureStore().dispatch({
            type: StoreActionType.SET_CHECK_OPERATOR_INFO,
            info: operatorData,
        });

        const refreshed = extractUsernameFromOperatorData(operatorData);
        if (!isMissingUsername(refreshed)) {
            return { ok: true, username: toEnglishLowerCase(refreshed) };
        }
    } catch (err) {
        console.error('[ensureOperatorUsername] Soft recovery failed', err);
    }

    await forceReloginForMissingUserContext();
    return { ok: false };
}
